import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { Link, useNavigate } from 'react-router-dom';
import { config } from '../../config/environment';
import { logger } from '../../utils/logger';
import { fetchCanvassStatus } from '../../utils/canvasses';
import { validateInterview, fetchInterviewBySessionId, submitInterviewThought } from '../../utils/interviews';
import styles from './BotsInterview.module.css';
import beepStatic from '../../assets/icons/augustsays/Beep-colour.svg';
import boopStatic from '../../assets/icons/augustsays/Boop-colour.svg';
import bopStatic from '../../assets/icons/augustsays/Bop.svg';
import spriteUrl from '../../assets/icons/mood/AS_Emotion_Icons_Sprite_v2.svg';
import { resolveTheme } from '../../themes';

const markdownComponents = {
  p: ({ children }) => <span>{children}</span>,
  strong: ({ children }) => <strong>{children}</strong>,
  em: ({ children }) => <em>{children}</em>,
};

const getLatestInterviewState = (interviewState) => {
  if (!interviewState) return null;
  if (Array.isArray(interviewState) && interviewState.length > 0) {
    return interviewState[interviewState.length - 1];
  }
  return interviewState;
};

const generateUUID = () =>
  'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });

const resetInterview = () => {
  try {
    const scope = `${window.location.pathname}`;
    localStorage.removeItem(`game_thread:${scope}`);
  } catch {}
  window.location.reload();
};

const TypingDots = () => (
  <span className={styles.typingDots}>
    <span></span>
    <span></span>
    <span></span>
  </span>
);

const BeepBot = ({ size = 120 }) => (
  <img src={beepStatic} alt="Beep" style={{ height: size, width: 'auto', display: 'block' }} />
);

const BoopBot = ({ size = 120 }) => (
  <img src={boopStatic} alt="Boop" style={{ height: size, width: 'auto', display: 'block' }} />
);

const BopBot = ({ size = 120 }) => (
  <img src={bopStatic} alt="Bop" style={{ height: size, width: 'auto', display: 'block' }} />
);

const ThemeBot = ({ theme, fallback, size = 120 }) =>
  theme.bot ? (
    <img src={theme.bot} alt="" style={{ height: size, width: 'auto', display: 'block' }} />
  ) : (
    fallback
  );

const QuestionBubble = ({ bot, text, small, loading = false, serious = false }) => (
  <div className={styles.botRow}>
    <div className={styles.botArt}>{bot}</div>
    <div className={`${styles.bubble} ${serious ? styles.bubbleSerious : ''}`}>
      {loading ? <TypingDots /> : <ReactMarkdown components={markdownComponents}>{text || ''}</ReactMarkdown>}
      {small && <small>{small}</small>}
    </div>
  </div>
);

const emotionPalette = {
  joy: { color: '#FFC7B4', label: 'joy' },
  pride: { color: '#B57BC0', label: 'pride' },
  belonging: { color: '#7FB0D9', label: 'belonging' },
  hope: { color: '#6FD3A6', label: 'hope' },
  nostalgia: { color: '#E8A98C', label: 'nostalgia' },
  frustration: { color: '#D19B6B', label: 'frustration' },
  nerves: { color: '#93A6B8', label: 'nerves' },
  habit: { color: '#B9B9B2', label: 'habit' }
};

const emotionMapping = {
  'positive': 'joy',
  'joy': 'joy',
  'amusement': 'joy',
  'sensory_pleasure': 'joy',
  'serenity': 'joy',
  'relief': 'joy',
  'satisfaction': 'joy',
  'euphoria': 'joy',
  'happy_for': 'joy',
  'hope': 'hope',
  'inspiration': 'hope',
  'awe': 'hope',
  'excitement': 'hope',
  'pride': 'pride',
  'admiration': 'pride',
  'elevation': 'pride',
  'gratitude': 'pride',
  'recognition': 'pride',
  'determination': 'pride',
  'fascination': 'pride',
  'positive_surprise': 'pride',
  'belonging': 'belonging',
  'psychological_safety': 'belonging',
  'affection': 'belonging',
  'tenderness': 'belonging',
  'lust': 'belonging',
  'frustration': 'frustration',
  'anger': 'frustration',
  'indignation': 'frustration',
  'resentment': 'frustration',
  'annoyance': 'frustration',
  'dissatisfaction': 'frustration',
  'contempt': 'frustration',
  'hate': 'frustration',
  'disgust': 'frustration',
  'nerves': 'nerves',
  'fear': 'nerves',
  'startle': 'nerves',
  'worry': 'nerves',
  'anxiety': 'nerves',
  'distrust': 'nerves',
  'doubt': 'nerves',
  'nervousness': 'nerves',
  'insecurity': 'nerves',
  'distress': 'nerves',
  'desperation': 'nerves',
  'confusion': 'nerves',
  'shock': 'nerves',
  'sadness': 'habit',
  'disappointment': 'habit',
  'pity': 'habit',
  'loneliness': 'habit',
  'rejection': 'habit',
  'humiliation': 'habit',
  'longing': 'habit',
  'envy': 'habit',
  'jealousy': 'habit',
  'guilt': 'habit',
  'regret': 'habit',
  'shame': 'habit',
  'embarrassment': 'habit',
  'boredom': 'habit',
  'reluctance': 'habit',
  'nostalgia': 'nostalgia',
  'trust': 'belonging',
  'safe': 'belonging',
  'safety': 'belonging',
  'autonomy': 'pride',
  'control': 'pride',
  'burnout': 'frustration',
  'exhausted': 'frustration'
};

const getSignatureColor = (emotionalTone, processedEmotions = []) => {
  if (!emotionalTone) return '#B9B9B2';
  const direct = processedEmotions.find((pe) =>
    pe.emotion === emotionalTone || pe.label === emotionalTone
  );
  if (direct?.color) return direct.color;
  const paletteKey = emotionMapping[emotionalTone] || emotionalTone;
  return emotionPalette[paletteKey]?.color || '#B9B9B2';
};

const spriteOverrideByEmotion = {
  calm: 'calm',
  serenity: 'calm',
  content: 'content',
  satisfied: 'satisfied',
  very_satisfied: 'very_satisfied',
  excited: 'excited',
  neutral: 'neutral',
  boredom: 'neutral',
  worried: 'worried',
  sadness: 'sad',
  disappointment: 'sad',
  pity: 'sad',
  loneliness: 'sad',
  rejection: 'sad',
  humiliation: 'sad',
  longing: 'sad',
  envy: 'sad',
  jealousy: 'sad',
  guilt: 'sad',
  regret: 'sad',
  shame: 'sad',
  embarrassment: 'sad',
  dissatisfaction: 'dissatisfied',
  frustrated: 'frustrated',
  contempt: 'very_dissatisfied',
  hate: 'very_dissatisfied',
  disgust: 'very_dissatisfied',
  sick: 'extremely_dissatisfied',
  stressed: 'stressed',
  fear: 'worried',
  worry: 'worried'
};

const paletteToSpriteId = {
  joy: 'satisfied',
  pride: 'very_satisfied',
  belonging: 'content',
  hope: 'excited',
  nostalgia: 'neutral',
  frustration: 'frustrated',
  nerves: 'stressed',
  habit: 'neutral'
};

const getSignatureSpriteId = (emotionalTone) => {
  if (!emotionalTone) return 'neutral';
  if (spriteOverrideByEmotion[emotionalTone]) return spriteOverrideByEmotion[emotionalTone];
  const paletteKey = emotionMapping[emotionalTone] || emotionalTone;
  return paletteToSpriteId[paletteKey] || 'neutral';
};

const getSignatureSize = (signatureElement) => {
  const [, intensity] = (signatureElement || 'circle_soft').split('_');
  return { soft: 34, medium: 42, bright: 50 }[intensity] || 32;
  // return { soft: 28, medium: 36, bright: 44 }[intensity] || 32;
};

let cachedSpriteText = null;
let spriteTextPromise = null;

const loadSpriteText = async () => {
  if (cachedSpriteText) return cachedSpriteText;
  if (!spriteTextPromise) {
    spriteTextPromise = fetch(spriteUrl).then((r) => r.text());
  }
  cachedSpriteText = await spriteTextPromise;
  return cachedSpriteText;
};

const SpriteIcon = ({ spriteId, color, size, opacity }) => {
  const [svgMarkup, setSvgMarkup] = useState(null);

  useEffect(() => {
    let mounted = true;

    const build = async () => {
      try {
        const text = await loadSpriteText();
        if (!mounted) return;

        const parser = new DOMParser();
        const doc = parser.parseFromString(text, 'image/svg+xml');
        const symbol = doc.getElementById(`emo-${spriteId}`);
        if (!symbol) {
          setSvgMarkup(null);
          return;
        }

        if (color) {
          const firstCircle = symbol.querySelector('circle');
          if (firstCircle) firstCircle.setAttribute('fill', color);
        }

        const viewBox = symbol.getAttribute('viewBox') || '0 0 120 120';
        setSvgMarkup(
          `<svg viewBox="${viewBox}" width="${size}" height="${size}" xmlns="http://www.w3.org/2000/svg">${symbol.innerHTML}</svg>`
        );
      } catch (e) {
        logger.warn('Failed to load emotion sprite icon', e);
        setSvgMarkup(null);
      }
    };

    build();
    return () => { mounted = false; };
  }, [spriteId, color, size]);

  if (!svgMarkup) return null;
  return (
    <span
      style={{ opacity, lineHeight: 0 }}
      // eslint-disable-next-line react/no-danger
      dangerouslySetInnerHTML={{ __html: svgMarkup }}
    />
  );
};

const SignatureIcon = ({ signatureElement, emotionalTone, visualIntensity, processedEmotions }) => {
  const color = getSignatureColor(emotionalTone, processedEmotions);
  const spriteId = getSignatureSpriteId(emotionalTone);
  const size = getSignatureSize(signatureElement);
  const opacity = Math.min(Math.max(visualIntensity ?? 0.6, 0.3), 1);
  return <SpriteIcon spriteId={spriteId} color={color} size={size} opacity={opacity} />;
};

const BotsInterview = ({
  runtimeUrl,
  interviewId,
  onPills,
  onIntroduction,
  onInterviewId,
  onShouldTerminate,
  autoStart = true,
  disabled = false,
  disabledPlaceholder = 'Thank you so much for your time and insights. (Bots)',
  introductionData,
  canvassData: canvassDataProp,
}) => {
  const voiceUrl = useMemo(() => {
    return `${config.interviewApiBase}/chat`;
  }, []);

  const navigate = useNavigate();

  const [threadId, setThreadId] = useState('');
  const [messages, setMessages] = useState([]);
  const [currentResponse, setCurrentResponse] = useState(null);
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [stage, setStage] = useState('preamble');
  const [turnCount, setTurnCount] = useState(0);
  const [isSending, setIsSending] = useState(false);
  const [input, setInput] = useState('');
  const [voiceCount, setVoiceCount] = useState(null);
  const [selectedAnswers, setSelectedAnswers] = useState([]);
  const [isValidating, setIsValidating] = useState(false);
  const [isSkipping, setIsSkipping] = useState(false);
  const [showEndInterviewModal, setShowEndInterviewModal] = useState(false);
  const [thoughtText, setThoughtText] = useState('');
  const [isSubmittingThought, setIsSubmittingThought] = useState(false);
  const [thoughtSubmitted, setThoughtSubmitted] = useState(false);
  const startedRef = useRef(false);
  const onSendRef = useRef(null);
  const firstPreambleAnswerRef = useRef(null);
  const containerRef = useRef(null);

  const startInterview = useCallback((openingText = null) => {
    setCanStart(true);
    setShowPreambleQuestions(false);
    setPreambleCompleted(true);
    setStage('beep');
    startedRef.current = true;

    setTimeout(() => {
      if (onSendRef.current) {
        onSendRef.current(null, openingText || 'start', true);
      }
    }, 50);
  }, []);

  const [canvassData, setCanvassData] = useState(null);
  const [canvassName, setCanvassName] = useState('');
  const [canvassDescription, setCanvassDescription] = useState('');
  const [preambleQuestions, setPreambleQuestions] = useState([]);
  const [preambleAnswers, setPreambleAnswers] = useState({});
  const [currentPreambleIndex, setCurrentPreambleIndex] = useState(-1);
  const [showPreambleQuestions, setShowPreambleQuestions] = useState(false);
  const [preambleCompleted, setPreambleCompleted] = useState(false);
  const [canStart, setCanStart] = useState(null);
  const [closingMessage, setClosingMessage] = useState(null);

  // Resolve client/organization theme from canvass data (default = August Says)
  const theme = useMemo(() => resolveTheme(canvassData || canvassDataProp), [canvassData, canvassDataProp]);

  // Ensure a per-game thread id
  useEffect(() => {
    try {
      const scope = `${window.location.pathname}`;
      const key = `game_thread:${scope}`;
      let existing = localStorage.getItem(key);
      if (!existing) {
        existing = generateUUID();
        localStorage.setItem(key, existing);
      }
      setThreadId(existing);
    } catch {
      setThreadId(generateUUID());
    }
  }, []);

  // Fetch canvass status and set up preamble questions
  useEffect(() => {
    let aborted = false;

    setPreambleQuestions([]);
    setPreambleAnswers({});
    setCurrentPreambleIndex(-1);
    setPreambleCompleted(false);
    setShowPreambleQuestions(false);
    firstPreambleAnswerRef.current = null;
    setCanStart(null);
    setStage('preamble');

    const applyCanvassStatus = (status) => {
      if (!status) { if (!aborted) setCanStart(false); return; }
      const statusValue = (status?.status || '').toLowerCase();
      const introductionText = status?.introduction_text || null;
      const savedClosingMessage = status?.closingMessage || null;

      if (!aborted) {
        if (introductionText && onIntroduction) {
          onIntroduction({ ...(introductionData || {}), introduction_text: introductionText });
        }
        if (status?.name) setCanvassName(status.name);
        if (status?.description) setCanvassDescription(status.description);
        setClosingMessage(savedClosingMessage);
        setCanvassData(status);

        const preambleQs = status?.preamble_questions || [];
        setPreambleQuestions(preambleQs);

        if (preambleQs.length > 0) {
          setShowPreambleQuestions(true);
          setCurrentPreambleIndex(0);
          setCanStart(false);
          setStage('preamble');
        } else if (statusValue === 'approved' || statusValue === 'active') {
          startInterview(null);
        } else {
          setCanStart(false);
          setShowPreambleQuestions(false);
          setStage('preamble');
        }
      }
    };

    if (canvassDataProp) {
      applyCanvassStatus(canvassDataProp);
      return;
    }

    const run = async () => {
      try {
        if (!interviewId) { if (!aborted) setCanStart(false); return; }
        const status = await fetchCanvassStatus({ canvassId: interviewId });
        applyCanvassStatus(status);
      } catch {
        if (!aborted) setCanStart(false);
      }
    };

    run();
    return () => { aborted = true; };
  }, [interviewId, canvassDataProp, startInterview]);

  // Load existing interview data if threadId exists (indicates existing interview)
  useEffect(() => {
    const loadExistingInterview = async () => {
      if (!threadId || !interviewId || messages.length > 0) return;

      try {
        console.log('[Existing Interview] Loading existing interview data for threadId:', threadId);

        // Check if this is an existing interview by looking at localStorage
        const scope = `${window.location.pathname}`;
        const key = `game_thread:${scope}`;
        const wasExistingBefore = localStorage.getItem(key) !== null;

        if (!wasExistingBefore) {
          console.log('[Existing Interview] This is a new interview, not loading existing data');
          return;
        }

        // Try to fetch existing interview data
        const interviewData = await fetchInterviewBySessionId({ sessionId: threadId });

        // Handle the actual response structure: { success: true, interview: {...}, messages: [...] }
        const existingMessages = interviewData?.messages || [];

        if (existingMessages && Array.isArray(existingMessages)) {
          console.log('[Existing Interview] Found existing interview with', existingMessages.length, 'messages');

          // Convert messages to frontend format and load them
          let lastAssistantParsed = null;
          let lastAssistantMessage = null;

          const tryParseAssistantJson = (raw) => {
            if (typeof raw !== 'string') return null;
            let trimmed = raw.trim();
            // If the value is itself a JSON string containing JSON, unwrap one level
            if (trimmed.startsWith('"') && trimmed.endsWith('"')) {
              try {
                const unwrapped = JSON.parse(trimmed);
                if (typeof unwrapped === 'string') trimmed = unwrapped.trim();
              } catch {
                // continue with original trimmed value
              }
            }
            if (!trimmed.startsWith('{') && !trimmed.startsWith('[')) return null;
            try {
              return JSON.parse(trimmed);
            } catch (e) {
              // Fallback: handle over-escaped strings by reversing common escape sequences once
              try {
                return JSON.parse(
                  trimmed.replace(/\\\\/g, '\\').replace(/\\"/g, '"').replace(/\\n/g, '\n')
                );
              } catch {
                return null;
              }
            }
          };

          const loadedMessages = existingMessages.map((msg) => {
            let content = msg.content;
            let parsedContent = null;
            let questionType = null;
            let answers = null;

            // Parse JSON content for assistant messages
            if (msg.role === 'assistant' && typeof msg.content === 'string') {
              parsedContent =
                tryParseAssistantJson(msg.content) ||
                tryParseAssistantJson(msg.userMessage) ||
                null;
              if (parsedContent && typeof parsedContent === 'object' && parsedContent.question) {
                content = parsedContent.question;
                questionType = parsedContent.question_type || null;
                answers = parsedContent.answers || null;
              }
            }

            if (msg.role === 'assistant') {
              lastAssistantParsed = parsedContent;
              lastAssistantMessage = msg;
            }

            return {
              role: msg.role,
              content: content,
              // Include emotion data if available
              ...(msg.sentimentEmotionMap && { sentimentEmotionMap: msg.sentimentEmotionMap }),
              ...(msg.interviewState && { interviewState: msg.interviewState }),
            };
          });

          setMessages((prev) => (prev.length === 0 ? loadedMessages : [...loadedMessages, ...prev]));

          // Restore interview UI state from the last assistant message and interview metadata
          const shouldTerminate =
            interviewData?.interview?.progress?.shouldTerminate === true ||
            interviewData?.interview?.state === 'completed';
          const metadataQuestionsCompleted = interviewData?.interview?.metadata?.questionsCompleted;
          const questionsCompleted =
            typeof metadataQuestionsCompleted === 'number'
              ? metadataQuestionsCompleted
              : loadedMessages.filter(
                  (m) => m.role === 'assistant' && typeof m.content === 'string' && m.content.trim().length > 0
                ).length;
          const nextTurnCount = questionsCompleted;

          if (lastAssistantMessage) {
            const current_question = lastAssistantParsed?.question || lastAssistantMessage.content;
            const question_type = lastAssistantParsed?.question_type;
            const answers = lastAssistantParsed?.answers;

            setCurrentResponse(
              (prev) =>
                prev ?? {
                  current_question,
                  question_type,
                  answers,
                  should_terminate: shouldTerminate,
                  interviewState: lastAssistantMessage.interviewState,
                  sentimentEmotionMap: lastAssistantMessage.sentimentEmotionMap,
                }
            );

            const isPredefinedLike =
              question_type === 'predefined' ||
              question_type === 'mixed' ||
              (answers && answers.length > 0);
            if (!shouldTerminate && isPredefinedLike) {
              setCurrentQuestion(
                (prev) =>
                  prev ?? {
                    question_type: question_type || 'predefined',
                    question_text: current_question,
                    answers,
                  }
              );
            } else {
              setCurrentQuestion((prev) => prev ?? null);
            }
          }

          setTurnCount((prev) => Math.max(prev, nextTurnCount));
          setStage((prev) => {
            if (shouldTerminate) return 'bop';
            if (nextTurnCount === 1) return 'beep';
            if (nextTurnCount > 1) return 'boop';
            return prev;
          });
          setCanStart((prev) => (shouldTerminate ? false : prev || true));
          setPreambleCompleted((prev) => prev || !shouldTerminate);
          setShowPreambleQuestions((prev) => (shouldTerminate ? false : prev && questionsCompleted === 0));

          console.log('[Existing Interview] Successfully loaded existing interview data');
        } else {
          console.log('[Existing Interview] No existing messages found');
        }
      } catch (error) {
        console.error('[Existing Interview] Error loading existing interview:', error);
        // Continue with new interview if loading fails
      }
    };

    loadExistingInterview();
  }, [threadId, interviewId, messages.length]);

  // Communicate interview state to the navigation bar
  useEffect(() => {
    const userReplyCount = messages.filter((m) => {
      if (m.role !== 'user' || typeof m.content !== 'string') return false;
      const lower = m.content.trim().toLowerCase();
      return lower !== 'start' && lower !== 'skip';
    }).length;
    const canEndInterview = userReplyCount >= 3 && !disabled && !isSending;
    window.dispatchEvent(
      new CustomEvent('interviewStateChanged', {
        detail: { canEndInterview, disabled, isSending, userReplyCount },
      })
    );
  }, [messages, disabled, isSending]);

  // Listen for end interview request from navigation bar
  useEffect(() => {
    const handleEndInterviewRequested = () => {
      setShowEndInterviewModal(true);
    };

    window.addEventListener('endInterviewRequested', handleEndInterviewRequested);

    return () => {
      window.removeEventListener('endInterviewRequested', handleEndInterviewRequested);
    };
  }, []);

  // Emit interview page detection event for NavigationBar
  useEffect(() => {
    window.dispatchEvent(new CustomEvent('interviewPageDetected', {
      detail: { isInterviewPage: true },
    }));

    return () => {
      window.dispatchEvent(new CustomEvent('interviewPageDetected', {
        detail: { isInterviewPage: false },
      }));
    };
  }, []);

  // Generate a random voice count once when reaching the Bop stage
  useEffect(() => {
    if (stage === 'bop' && voiceCount === null) {
      setVoiceCount(31 + Math.floor(Math.random() * 40));
    }
  }, [stage, voiceCount]);

  // Scroll back to the top when a new assistant response/question arrives
  useEffect(() => {
    if (!currentResponse || !containerRef.current) return;

    const findScrollableParent = (el) => {
      let parent = el.parentElement;
      while (parent) {
        const style = window.getComputedStyle(parent);
        const overflow = `${style.overflow} ${style.overflowY}`;
        if (/(auto|scroll)/.test(overflow)) return parent;
        parent = parent.parentElement;
      }
      return window;
    };

    const scrollable = findScrollableParent(containerRef.current);
    if (scrollable === window) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      scrollable.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [currentResponse]);

  const appendMessage = (role, content, interviewState, sentimentEmotionMap) => {
    if (!content) return;
    setMessages((prev) => [
      ...prev,
      { role, content, interviewState, sentimentEmotionMap },
    ]);
  };

  const extractAssistantReply = (respJson) => {
    const currentQuestion = respJson?.current_question;
    if (currentQuestion) {
      return currentQuestion;
    }
    const g = respJson?.data?.generateCopilotResponse;
    if (g?.messages && Array.isArray(g.messages)) {
      for (let i = g.messages.length - 1; i >= 0; i--) {
        const m = g.messages[i];
        if (m?.role === 'assistant') {
          if (Array.isArray(m.content)) return m.content.join('\n');
          if (typeof m.content === 'string') return m.content;
        }
      }
    }
    return null;
  };

  const processExtensions = (data) => {
    try {
      setCurrentResponse(data);

      const isPredefinedLike =
        data.question_type === 'predefined' ||
        data.question_type === 'mixed' ||
        (data.answers && data.answers.length > 0);

      if (!data.should_terminate && isPredefinedLike) {
        setCurrentQuestion(
          data.question
            ? { ...data.question, question_type: data.question_type || 'predefined' }
            : {
                question_type: data.question_type || 'predefined',
                question_text: data.current_question,
                answers: data.answers,
              }
        );
      } else {
        setCurrentQuestion(null);
      }

      const nextTurnCount = turnCount + 1;
      setTurnCount(nextTurnCount);

      if (data.should_terminate) {
        setStage('bop');
        if (onShouldTerminate) onShouldTerminate(true);
        try {
          const scope = `${window.location.pathname}`;
          localStorage.removeItem(`game_thread:${scope}`);
        } catch {}
      } else {
        setStage(nextTurnCount === 1 ? 'beep' : 'boop');
      }

      if (data.pills && onPills) onPills(data.pills);
      if (data.introduction && onIntroduction) onIntroduction(data.introduction);
      if ((data.interview_id || data.thread_id) && onInterviewId) {
        onInterviewId(data.interview_id || data.thread_id);
      }
      if (typeof data.voiceCount === 'number') {
        setVoiceCount(data.voiceCount);
      }
    } catch (err) {
      logger.error('Failed to process extensions:', err);
    }
  };

  const buildRequestBody = (userText, predefinedAnswer = null, selectedAnswers = []) => {
    const messagesToSend = messages
      .map((msg) => ({ role: msg.role, content: msg.content }))
      .filter((msg) => msg.role && msg.content);

    messagesToSend.push({
      role: 'user',
      content: userText,
      userMessage: userText,
      predefinedAnswer: predefinedAnswer || null,
      selectedAnswers: selectedAnswers.length > 0 ? selectedAnswers : null
    });

    const body = {
      threadId: threadId,
      messages: messagesToSend,
      voiceId: interviewId,
      environment: config.environment,
      frontendUrl: config.frontendUrl,
    };

    if (Object.keys(preambleAnswers).length > 0 && !showPreambleQuestions && messages.length === 0) {
      body.preambleAnswers = preambleAnswers;
    }

    if (predefinedAnswer) {
      body.predefinedAnswer = predefinedAnswer;
    }

    if (selectedAnswers.length > 0) {
      body.selectedAnswers = selectedAnswers;
    }

    return body;
  };

  const onSend = async (e, overrideText, bypassCanStartCheck = false, predefinedAnswer = null) => {
    e?.preventDefault?.();

    if (disabled || (!bypassCanStartCheck && canStart !== true)) {
      return;
    }

    const selectedText = selectedAnswers.map((a) => a.label).join(', ');
    const combinedText = [selectedText, overrideText !== undefined ? String(overrideText) : input.trim()]
      .filter(Boolean)
      .join('. ')
      .trim();
    const text = overrideText !== undefined ? String(overrideText).trim() : combinedText;
    if (!text || isSending) {
      return;
    }

    // Avoid sending a duplicate 'start' if an existing interview was already loaded
    if (bypassCanStartCheck && text.toLowerCase() === 'start' && messages.length > 0) {
      return;
    }

    const answersToSend = selectedAnswers;
    setSelectedAnswers([]);
    setIsSending(true);
    appendMessage('user', text);
    if (overrideText === undefined) {
      setInput('');
    }

    try {
      const body = buildRequestBody(text, answersToSend[0] || predefinedAnswer, answersToSend);
      const headers = { 'Content-Type': 'application/json' };

      const functionsKey = process.env.REACT_APP_INTERVIEW_API_KEY;
      if (functionsKey) {
        headers['x-functions-key'] = functionsKey;
      }

      const res = await fetch(voiceUrl, {
        method: 'POST',
        headers,
        body: JSON.stringify(body),
      });

      const data = await res.json();
      const assistant = extractAssistantReply(data);
      if (!assistant) {
        logger.warn('Incomplete assistant reply; resetting interview.');
        resetInterview();
        return;
      }
      appendMessage('assistant', assistant, data.interviewState, data.sentimentEmotionMap);
      processExtensions(data);
    } catch (err) {
      logger.error('BotsInterview send failed:', err);
      resetInterview();
      return;
    } finally {
      setIsSending(false);
    }
  };

  useEffect(() => {
    onSendRef.current = onSend;
  }, [onSend]);

  const handlePillClick = (answer) => {
    setSelectedAnswers((prev) => {
      const exists = prev.some((a) => a.label === answer.label);
      if (exists) {
        return prev.filter((a) => a.label !== answer.label);
      }
      return [...prev, answer];
    });
  };

  const handleSkip = async () => {
    setIsSkipping(true);
    try {
      await onSend(null, 'skip');
    } finally {
      setIsSkipping(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      onSend(e);
    }
  };

  const handleRestart = () => resetInterview();

  const handleThoughtSubmit = async (e) => {
    e.preventDefault();
    const sessionId =
      currentResponse?.interview_id ||
      currentResponse?.data?.generateCopilotResponse?.extensions?.interview_id ||
      threadId;

    if (!sessionId || !thoughtText.trim()) {
      return;
    }

    if (isSubmittingThought) return;
    setIsSubmittingThought(true);

    try {
      await submitInterviewThought({
        sessionId,
        canvassId: interviewId,
        text: thoughtText,
      });
      setThoughtSubmitted(true);
      setThoughtText('');
    } catch (err) {
      logger.error('Failed to submit thought:', err);
    } finally {
      setIsSubmittingThought(false);
    }
  };

  const handleValidationResponse = async (isUserValidated) => {
    const sessionId =
      currentResponse?.interview_id ||
      currentResponse?.data?.generateCopilotResponse?.extensions?.interview_id ||
      threadId;

    if (!sessionId) {
      return;
    }

    if (isValidating) return;
    setIsValidating(true);

    try {
      await validateInterview({ sessionId, isUserValidated });
    } catch (err) {
      logger.error('Failed to validate interview:', err);
    } finally {
      setIsValidating(false);
      if (isUserValidated) {
        navigate('/voice/complete');
      } else {
        const interviewUrl = window.location.href;
        navigate(`/voice/completed?interviewUrl=${encodeURIComponent(interviewUrl)}`);
      }
    }
  };

  const handlePreambleAnswer = (answerIndex) => {
    const currentQuestion = preambleQuestions[currentPreambleIndex];
    const answer = currentQuestion.answers[answerIndex];
    const answerKey = currentQuestion.question_text || currentPreambleIndex;

    setPreambleAnswers((prev) => ({
      ...prev,
      [answerKey]: {
        question: currentQuestion.question_text,
        answer: answer.label,
        anchor: answer.anchor,
        valence: answer.valence,
        answerIndex,
      },
    }));

    if (currentPreambleIndex === 0) {
      firstPreambleAnswerRef.current = answer.label;
    }

    if (currentPreambleIndex < preambleQuestions.length - 1) {
      setCurrentPreambleIndex(currentPreambleIndex + 1);
    } else {
      startInterview(firstPreambleAnswerRef.current || 'start');
    }
  };

  const isBeep = stage === 'beep';
  const isBoop = stage === 'boop';
  const isBop = stage === 'bop';
  const isPreamble = stage === 'preamble';
  const isLoading = isSending && !currentResponse;
  const isMixed = currentQuestion?.question_type === 'mixed';
  const isPredefined = currentQuestion !== null && !isBop;
  const isOpen = (currentQuestion === null || isMixed) && !isBop && currentResponse !== null && !isLoading;
  const isFirstBoop = isBoop && turnCount === 2;

  let smallText = '';
  if (isBop) {
    smallText = 'Your words go upstairs tonight — exactly as you said them.';
  } else if (isLoading) {
    smallText = 'Crafting your next question — this conversation is yours to own.';
  } else if (isBeep) {
    smallText = isMixed
      ? 'Tap all that fit and add your thoughts.'
      : isPredefined
      ? 'Tap all that fit and add your thoughts.'
      : 'Whatever you say goes somewhere real. My friend Boop will prove it at the end.';
  } else if (isBoop) {
    smallText = isMixed
      ? 'Tap all that fit and add your thoughts.'
      : isPredefined
      ? 'Tap all that fit and add your thoughts.'
      : 'Say it how you\'d say it. No forms here.';
  }

  const latestInterviewState = getLatestInterviewState(currentResponse?.interviewState);
  const processedEmotions = latestInterviewState?.processed_emotions || [];
  // const showMirror = isBoop && processedEmotions.length > 0;
  const showMirror = false;
  const mirrorText = latestInterviewState
    ? `We're hearing a ${latestInterviewState.emotional_tone} signal around ${latestInterviewState.cluster_progress}.`
    : '';

  const lastUserMessage = messages
    .slice()
    .reverse()
    .find(
      (m) =>
        m.role === 'user' &&
        typeof m.content === 'string' &&
        m.content.trim().toLowerCase() !== 'start' &&
        m.content.trim().toLowerCase() !== 'skip'
    );
  const verbatim = currentResponse?.userUnderstandingSummary || lastUserMessage?.content || '';
  const showVerbatim = !!verbatim.trim();

  const rEmotion = latestInterviewState?.emotional_tone || '—';
  const rDriver = latestInterviewState?.cluster_progress || '—';

  const preambleScreenClass = `${styles.screen} ${isPreamble ? styles.screenOn : ''}`;
  const beepScreenClass = `${styles.screen} ${isBeep ? styles.screenOn : ''}`;
  const boopScreenClass = `${styles.screen} ${isBoop ? styles.screenOn : ''}`;
  const bopScreenClass = `${styles.screen} ${isBop ? styles.screenOn : ''}`;

  const beepStopClass = `${styles.stop} ${isBeep ? styles.stopActive : ''} ${isBoop || isBop ? styles.stopDone : ''}`;
  const boopStopClass = `${styles.stop} ${isBoop ? styles.stopActive : ''} ${isBop ? styles.stopDone : ''}`;
  const bopStopClass = `${styles.stop} ${isBop ? styles.stopActive : ''}`;

  const renderPills = () => (
    <div className={styles.pills}>
      {currentQuestion.answers.map((answer, idx) => {
        // const isNeg = typeof answer.valence === 'number' && answer.valence < 0;
        const isNeg = false;
        const isSelected = selectedAnswers.some((a) => a.label === answer.label);
        return (
          <button
            key={idx}
            className={`${styles.pill} ${isNeg ? styles.pillNeg : ''} ${
              isSelected ? (isNeg ? styles.pillNegSel : styles.pillSel) : ''
            }`}
            disabled={isSending || disabled}
            onClick={() => handlePillClick(answer)}
          >
            {answer.label}
          </button>
        );
      })}
    </div>
  );

  const renderActionButtons = () => {
    const sendText = isBeep ? 'Send' : 'That\'s what I mean';
    const hasAnswer = input.trim().length > 0 || selectedAnswers.length > 0;
    return (
      <div className={styles.row}>
        <button
          className={styles.btn}
          onClick={() => onSend(null)}
          disabled={isSending || disabled || !hasAnswer}
        >
          {isSending ? 'Sending…' : sendText}
        </button>
        {/* <button
          className={`${styles.btn} ${styles.btnGhost}`}
          onClick={handleSkip}
          disabled={isSending || disabled || isSkipping}
        >
          {isSkipping ? 'Skipping…' : 'Skip this one'}
        </button> */}
      </div>
    );
  };

  const renderOpenWrap = () => {
    const placeholder = isBeep
      ? 'Say it how you\'d say it. No forms here.'
      : 'Feeling something else, feel free to share.';
    return (
      <div className={styles.openWrap}>
        <textarea
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={isSending || disabled}
        />
        {renderActionButtons()}
      </div>
    );
  };

  return (
    <div ref={containerRef} className={styles.container} style={theme.cssVars} data-theme={theme.key}>
      <header className={styles.header}>
        <div className={styles.wordmark}>
          {theme.logo ? (
            <>
              <img src={theme.logo} alt={theme.key} className={styles.themeLogo} />
              {theme.headerTagline && <span>{theme.headerTagline}</span>}
            </>
          ) : (
            <>
              August Says
              <span>The Bots Experience</span>
            </>
          )}
        </div>
        <div className={styles.tracker}>  
          {theme.tracker ? (
            <>
              <div className={beepStopClass}>
                <img src={theme.tracker.avatar} alt="" className={styles.trackerAvatar} />
                {theme.tracker.showLabels && <label>Beep</label>}
              </div>
              <div className={boopStopClass}>
                <img src={theme.tracker.avatar} alt="" className={styles.trackerAvatar} />
                {theme.tracker.showLabels && <label>Boop</label>}
              </div>
              <div className={bopStopClass}>
                <img src={theme.tracker.avatar} alt="" className={styles.trackerAvatar} />
                {theme.tracker.showLabels && <label>Bop</label>}
              </div>
            </>
          ) : (
            <>
              <div className={beepStopClass}>
                <BeepBot size={34} />
                <label>Beep</label>
              </div>
              <div className={boopStopClass}>
                <BoopBot size={34} />
                <label>Boop</label>
              </div>
              <div className={bopStopClass}>
                <BopBot size={34} />
                <label>Bop</label>
              </div>
            </>
          )}
          {!currentResponse?.should_terminate && (
            <div className={styles.progress}>
              <div className={styles.progressTrack}>
                <div
                  className={styles.progressFill}
                  style={{ width: `${Math.min((turnCount / 8) * 100, 100)}%` }}
                />
              </div>
              <label>progress</label>
            </div>
          )}
        </div>
      </header>
      <>
        {/* <div className={styles.intro}>{defaultBody}</div> */}
        <div className={`${styles.privacyReminder} ${styles.header}`}>
          Privacy Reminder: Please don’t share personal information (PII) in your responses. Avoid names, emails, phone numbers, addresses, IDs, or anything that could identify you.
        </div>
        <div className={`${styles.privacyReminder}`}>
          <Link to="/data-privacy" className={styles.privacyLink}>Data Policy</Link>
        </div>
      </>

      <main className={styles.main}>
        {/* Preamble screen */}
        <section className={preambleScreenClass}>
          {showPreambleQuestions && currentPreambleIndex >= 0 && currentPreambleIndex < preambleQuestions.length && (
            <>
              <QuestionBubble
                bot={<ThemeBot theme={theme} fallback={<BeepBot />} />}
                text={preambleQuestions[currentPreambleIndex].question_text}
              />
              <div className={styles.pills}>
                {preambleQuestions[currentPreambleIndex].answers.map((answer, idx) => (
                  <button
                    key={idx}
                    className={styles.pill}
                    onClick={() => handlePreambleAnswer(idx)}
                    disabled={isSending || disabled}
                  >
                    {answer.label}
                  </button>
                ))}
              </div>
            </>
          )}

          {canStart === null && !showPreambleQuestions && (
            <QuestionBubble bot={<ThemeBot theme={theme} fallback={<BeepBot />} />} loading small="Loading your interview…" />
          )}

          {canStart === false && !showPreambleQuestions && (
            <QuestionBubble
              bot={<ThemeBot theme={theme} fallback={<BeepBot />} />}
              text="This interview is not available."
              small="Please check the link and try again."
              serious
            />
          )}
        </section>

        {/* Beep screen */}
        <section className={beepScreenClass}>
          <QuestionBubble
            bot={<ThemeBot theme={theme} fallback={<BeepBot />} />}
            text={currentResponse?.current_question}
            small={smallText}
            loading={isLoading && !currentResponse}
          />
          {isPredefined && renderPills()}
          {isPredefined && !isMixed && selectedAnswers.length > 0 && renderActionButtons()}
          {isOpen && renderOpenWrap()}
        </section>

        {/* Boop screen */}
        <section className={boopScreenClass}>
          {isFirstBoop && (
            <div className={styles.handoff}>Beep passed you to Boop — the listener</div>
          )}
          <QuestionBubble
            bot={<ThemeBot theme={theme} fallback={<BoopBot />} />}
            text={currentResponse?.current_question}
            small={smallText}
          />
          {showMirror && (
            <div className={styles.mirror}>
              <div className={styles.mirrorSignature}>
                <SignatureIcon
                  signatureElement={latestInterviewState?.signature_element}
                  emotionalTone={latestInterviewState?.emotional_tone}
                  visualIntensity={latestInterviewState?.visual_intensity}
                  processedEmotions={processedEmotions}
                />
              </div>
              <div className={styles.what}>
                <b>What we're hearing</b>
                <span>{mirrorText}</span>
              </div>
            </div>
          )}
          {isPredefined && renderPills()}
          {isPredefined && !isMixed && selectedAnswers.length > 0 && renderActionButtons()}
          {isOpen && renderOpenWrap()}
        </section>

        {/* Bop screen */}
        <section className={bopScreenClass}>
          <div className={styles.handoff}>Boop handed your words to Bop — the courier</div>
          <QuestionBubble
            bot={<ThemeBot theme={theme} fallback={<BopBot />} />}
            text="I've got it from here. **Nothing gets edited, nothing gets lost.**"
            small={smallText}
          />
          {/* {currentResponse?.current_question && (
            <div
              className={styles.bubble}
              style={{
                margin: '20px 0 0 140px',
                borderBottomLeftRadius: 'var(--radius)',
                borderTopLeftRadius: 6,
              }}
            >
              <ReactMarkdown components={markdownComponents}>
                {currentResponse.current_question}
              </ReactMarkdown>
            </div>
          )} */}

          <div className={styles.receipt}>
            <h4>Your signal — read it back</h4>
            {/* <div className={styles.line}>
              <b>You felt</b>
              <span className={styles.chip}>{rEmotion}</span>
            </div>
            <div className={styles.line}>
              <b>Shaped by</b>
              <span className={styles.chip}>{rDriver}</span>
            </div> */}
            {showVerbatim && (
              <div className={styles.line}>
                <b>You said</b>
                <span className={styles.verbatim}>
                  <ReactMarkdown components={markdownComponents}>{verbatim}</ReactMarkdown>
                </span>
              </div>
            )}
          </div>

          <div className={styles.loopCard}>
            <b>You said → We did</b>
            <p>
              {closingMessage || <>We are listening and will act on the information.</>}
            </p>
          </div>

          {/* <div className={styles.voiceNum}>
            You're voice <b>#{Math.max(1, voiceCount || 0)}</b> today. Every one of them travels.
          </div> */}

          {/* <div className={styles.row} style={{ justifyContent: 'center', marginTop: 22 }}>
            <button
              className={`${styles.btn} ${styles.btnPinkish}`}
              onClick={() => handleValidationResponse(true)}
              disabled={isValidating}
            >
              Yes, looks right
            </button>
            <button
              className={`${styles.btn} ${styles.btnGhost}`}
              onClick={() => handleValidationResponse(false)}
              disabled={isValidating}
            >
              No, not quite
            </button>
          </div> */}

          <div style={{ textAlign: 'center' }}>
            <button className={styles.restart} onClick={handleRestart}>
              ↺ Start over
            </button>
          </div>

          {!thoughtSubmitted ? (
            <form onSubmit={handleThoughtSubmit} className={styles.receipt}>
              <h4>Anything else?</h4>
              <p style={{ margin: '0 0 16px', fontSize: 15, lineHeight: 1.5 }}>
                Your own words help. Add anything that wasn't captured.
              </p>
              <div className={styles.openWrap}>
                <textarea
                  value={thoughtText}
                  onChange={(e) => setThoughtText(e.target.value)}
                  placeholder="What else should we know?"
                  disabled={isSubmittingThought}
                />
              </div>
              <div className={styles.row} style={{ justifyContent: 'flex-end', marginTop: 12 }}>
                <button
                  type="submit"
                  className={`${styles.btn} ${styles.btnPinkish}`}
                  disabled={isSubmittingThought || !thoughtText.trim()}
                >
                  {isSubmittingThought ? 'Sending…' : 'Send'}
                </button>
              </div>
            </form>
          ) : (
            <div className={styles.loopCard}>
              <b>Thank you</b>
              <p>We’ve saved your extra thoughts with this interview.</p>
            </div>
          )}
        </section>
      </main>

      <footer className={styles.footer}>
        {theme.footer || 'August Says · The Bots Experience · Beep greets, Boop listens, Bop delivers'}
      </footer>

      {showEndInterviewModal && (
        <div className={styles.endInterviewOverlay} role="presentation">
          <div className={styles.endInterviewModal} role="dialog" aria-modal="true" aria-label="End Interview Confirmation">
            <h2>End Interview?</h2>
            <p>Are you sure you want to end the interview? Your insights are valuable and help us understand real experiences better.</p>
            <p>If you have more to share, continuing for just a few more questions can provide much richer insights. But you're in control - you can end whenever you feel heard.</p>
            <div className={styles.row}>
              <button
                className={`${styles.btn} ${styles.btnGhost}`}
                onClick={() => setShowEndInterviewModal(false)}
              >
                Continue Sharing
              </button>
              <button
                className={styles.btn}
                onClick={() => {
                  setShowEndInterviewModal(false);
                  onSend(null, 'End the Interview');
                }}
              >
                Yes, End Interview
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BotsInterview;
